import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Box,
  Typography
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

const maintenanceTypes = [
  { value: 'routine', label: 'Routine Maintenance' },
  { value: 'repair', label: 'Repair' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'calibration', label: 'Calibration' },
  { value: 'replacement', label: 'Part Replacement' },
  { value: 'upgrade', label: 'Upgrade' }
];

const AddMaintenanceLogDialog = ({
  open,
  onClose,
  onSuccess,
  droneId,
  organizationUuid,
  droneName
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    maintenanceType: 'routine',
    description: '',
    performedDate: new Date().toISOString().split('T')[0], // Today's date
    nextMaintenanceDate: '',
    cost: '',
    costCurrency: 'USD',
    hoursAtMaintenance: '',
    flightsAtMaintenance: '',
    partsUsed: '',
    notes: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.maintenanceType || !formData.description || !formData.performedDate) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();

      // Prepare request body (only include non-empty fields)
      const requestBody = {
        maintenanceType: formData.maintenanceType,
        description: formData.description,
        performedDate: formData.performedDate,
        ...(formData.nextMaintenanceDate && { nextMaintenanceDate: formData.nextMaintenanceDate }),
        ...(formData.cost && { cost: parseFloat(formData.cost) }),
        ...(formData.costCurrency && { costCurrency: formData.costCurrency }),
        ...(formData.hoursAtMaintenance && { hoursAtMaintenance: parseFloat(formData.hoursAtMaintenance) }),
        ...(formData.flightsAtMaintenance && { flightsAtMaintenance: parseInt(formData.flightsAtMaintenance) }),
        ...(formData.partsUsed && { partsUsed: formData.partsUsed }),
        ...(formData.notes && { notes: formData.notes })
      };


      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${organizationUuid}/drones/${droneId}/maintenance`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        }
      );


      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to add maintenance log');
      }

      // Success - reset form and notify parent
      setFormData({
        maintenanceType: 'routine',
        description: '',
        performedDate: new Date().toISOString().split('T')[0],
        nextMaintenanceDate: '',
        cost: '',
        costCurrency: 'USD',
        hoursAtMaintenance: '',
        flightsAtMaintenance: '',
        partsUsed: '',
        notes: ''
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form on close
      setFormData({
        maintenanceType: 'routine',
        description: '',
        performedDate: new Date().toISOString().split('T')[0],
        nextMaintenanceDate: '',
        cost: '',
        costCurrency: 'USD',
        hoursAtMaintenance: '',
        flightsAtMaintenance: '',
        partsUsed: '',
        notes: ''
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Add Maintenance Log
        {droneName && (
          <Typography variant="body2" color="textSecondary">
            {droneName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Maintenance Type */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                required
                label="Maintenance Type"
                name="maintenanceType"
                value={formData.maintenanceType}
                onChange={handleChange}
              >
                {maintenanceTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Performed Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Performed Date"
                name="performedDate"
                value={formData.performedDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the maintenance work performed..."
              />
            </Grid>

            {/* Next Maintenance Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Next Maintenance Due Date"
                name="nextMaintenanceDate"
                value={formData.nextMaintenanceDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                helperText="Optional: When is the next maintenance due?"
              />
            </Grid>

            {/* Cost */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Cost"
                name="cost"
                value={formData.cost}
                onChange={handleChange}
                inputProps={{ step: '0.01', min: '0' }}
                placeholder="0.00"
              />
            </Grid>

            {/* Currency */}
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Currency"
                name="costCurrency"
                value={formData.costCurrency}
                onChange={handleChange}
                placeholder="USD"
              />
            </Grid>

            {/* Flight Hours at Maintenance */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Flight Hours at Maintenance"
                name="hoursAtMaintenance"
                value={formData.hoursAtMaintenance}
                onChange={handleChange}
                inputProps={{ step: '0.1', min: '0' }}
                helperText="Total flight hours when maintenance was performed"
              />
            </Grid>

            {/* Total Flights at Maintenance */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Total Flights at Maintenance"
                name="flightsAtMaintenance"
                value={formData.flightsAtMaintenance}
                onChange={handleChange}
                inputProps={{ step: '1', min: '0' }}
                helperText="Total flight count when maintenance was performed"
              />
            </Grid>

            {/* Parts Used */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Parts Used"
                name="partsUsed"
                value={formData.partsUsed}
                onChange={handleChange}
                placeholder="e.g., Propellers, Battery, Motor..."
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Additional Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any additional notes or observations..."
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Adding...' : 'Add Maintenance Log'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMaintenanceLogDialog;
